/**
 * UnifiedCoordinateSystem - Single source of truth for all coordinates
 * 
 * Eliminates coordinate transformation bugs by standardizing on CANVAS space
 * All positioning calculations use canvas pixels (0,0 = top-left of canvas)
 * 
 * Based on analysis of EdgeDocs typesetter response:
 * - Character boxes are in PAGE space (same as CANVAS space)
 * - Frame coordinates match margin offsets
 * - No transformation needed for character box comparisons
 */

export enum CoordinateSpace {
  CANVAS = 'CANVAS',     // Canvas pixels (0,0 = top-left of canvas)
  PAGE = 'PAGE',         // Same as canvas for EdgeDocs (verified from typesetter)
  CONTENT = 'CONTENT',   // Relative to content area (margins applied) 
  BACKEND = 'BACKEND',   // Raw backend coordinates (same as PAGE)
  HARFBUZZ = 'HARFBUZZ', // HarfBuzz typesetter coordinate space
  VIEWPORT = 'VIEWPORT', // Viewport coordinate space
  DEVICE = 'DEVICE'      // Device coordinate space
}

export interface Coordinates {
  x: number;
  y: number;
  space: CoordinateSpace;
}

export interface CanvasCoordinate {
  x: number;
  y: number;
  space: CoordinateSpace.CANVAS;
  precision: 'pixel' | 'subpixel';
  timestamp?: number;
}

export interface LayoutContext {
  contentRect: { x: number; y: number; width: number; height: number };
  margins: { top: number; right: number; bottom: number; left: number };
  dpi: number;
  pageSize: { width: number; height: number };
  planHash?: string;
}

export interface CoordinateValidation {
  isValid: boolean;
  space: CoordinateSpace;
  bounds?: { minX: number; maxX: number; minY: number; maxY: number };
  errors?: string[];
}

export class UnifiedCoordinateSystem {
  
  // ============================================================================
  // COORDINATE CONVERSION
  // ============================================================================
  
  /**
   * Convert any coordinate to canvas space (primary coordinate system)
   * 
   * Based on EdgeDocs typesetter analysis:
   * - Character boxes at [108, 108] match frame.x=108 and margins.left=108
   * - This confirms character boxes are already in PAGE/CANVAS space
   * - No transformation needed for most coordinates
   */
  static toCanvas(
    coord: { x: number; y: number; space?: CoordinateSpace },
    context: LayoutContext
  ): CanvasCoordinate {
    const timestamp = performance.now();
    
    switch (coord.space) {
      case CoordinateSpace.CONTENT:
        // Content space needs offset by content rect
        return {
          x: coord.x + context.contentRect.x,
          y: coord.y + context.contentRect.y,
          space: CoordinateSpace.CANVAS,
          precision: 'pixel',
          timestamp
        };
      
      case CoordinateSpace.BACKEND:
      case CoordinateSpace.PAGE:
        // Backend/Page coordinates are already in canvas space (verified)
        return {
          x: coord.x,
          y: coord.y,
          space: CoordinateSpace.CANVAS,
          precision: 'subpixel',
          timestamp
        };
      
      case CoordinateSpace.HARFBUZZ:
        // ✅ CARET FIX: Backend now sends correct pixel coordinates after font size fix
        // No scaling needed - coordinates are already in Canvas space
        return {
          x: coord.x,
          y: coord.y,
          space: CoordinateSpace.CANVAS,
          precision: 'subpixel',
          timestamp
        };
      
      case CoordinateSpace.VIEWPORT:
        // Viewport space - same as canvas for now
        return {
          x: coord.x,
          y: coord.y,
          space: CoordinateSpace.CANVAS,
          precision: 'pixel',
          timestamp
        };
      
      case CoordinateSpace.DEVICE:
        // Device space - same as canvas for now  
        return {
          x: coord.x,
          y: coord.y,
          space: CoordinateSpace.CANVAS,
          precision: 'pixel',
          timestamp
        };
        
      case CoordinateSpace.CANVAS:
      default:
        // Already in canvas space or unspecified (assume canvas)
        return {
          x: coord.x,
          y: coord.y,
          space: CoordinateSpace.CANVAS,
          precision: 'pixel',
          timestamp
        };
    }
  }
  
  /**
   * Convert canvas coordinate to content space
   */
  static toContent(
    canvasCoord: CanvasCoordinate,
    context: LayoutContext
  ): CanvasCoordinate {
    return {
      x: canvasCoord.x - context.contentRect.x,
      y: canvasCoord.y - context.contentRect.y,
      space: CoordinateSpace.CANVAS, // Still canvas space, just offset
      precision: canvasCoord.precision,
      timestamp: performance.now()
    };
  }
  
  // ============================================================================
  // VALIDATION
  // ============================================================================
  
  /**
   * Validate coordinate is in expected space and bounds
   */
  static validateCanvas(coord: CanvasCoordinate, context?: LayoutContext): CoordinateValidation {
    const errors: string[] = [];
    
    // Basic validation
    if (coord.space !== CoordinateSpace.CANVAS) {
      errors.push(`Expected CANVAS space, got ${coord.space}`);
    }
    
    if (typeof coord.x !== 'number' || typeof coord.y !== 'number') {
      errors.push('Coordinates must be numbers');
    }
    
    if (isNaN(coord.x) || isNaN(coord.y)) {
      errors.push('Coordinates cannot be NaN');
    }
    
    // Bounds validation if context provided
    if (context) {
      const bounds = {
        minX: 0,
        maxX: context.pageSize.width,
        minY: 0,
        maxY: context.pageSize.height
      };
      
      if (coord.x < bounds.minX || coord.x > bounds.maxX) {
        errors.push(`X coordinate ${coord.x} outside page bounds [${bounds.minX}, ${bounds.maxX}]`);
      }
      
      if (coord.y < bounds.minY || coord.y > bounds.maxY) {
        errors.push(`Y coordinate ${coord.y} outside page bounds [${bounds.minY}, ${bounds.maxY}]`);
      }
      
      return {
        isValid: errors.length === 0,
        space: coord.space,
        bounds,
        errors: errors.length > 0 ? errors : undefined
      };
    }
    
    return {
      isValid: errors.length === 0,
      space: coord.space,
      errors: errors.length > 0 ? errors : undefined
    };
  }
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  /**
   * Calculate distance between two canvas coordinates
   */
  static distance(coord1: CanvasCoordinate, coord2: CanvasCoordinate): number {
    return Math.sqrt(
      (coord1.x - coord2.x) ** 2 + (coord1.y - coord2.y) ** 2
    );
  }
  
  /**
   * Check if coordinate is within bounds
   */
  static isWithinBounds(
    coord: CanvasCoordinate, 
    bounds: { x: number; y: number; width: number; height: number }
  ): boolean {
    return coord.x >= bounds.x && 
           coord.x < bounds.x + bounds.width &&
           coord.y >= bounds.y && 
           coord.y < bounds.y + bounds.height;
  }
  
  /**
   * Clamp coordinate to bounds
   */
  static clampToBounds(
    coord: CanvasCoordinate,
    bounds: { x: number; y: number; width: number; height: number }
  ): CanvasCoordinate {
    return {
      x: Math.max(bounds.x, Math.min(coord.x, bounds.x + bounds.width)),
      y: Math.max(bounds.y, Math.min(coord.y, bounds.y + bounds.height)),
      space: CoordinateSpace.CANVAS,
      precision: coord.precision,
      timestamp: performance.now()
    };
  }
  
  /**
   * Create canvas coordinate from mouse event
   */
  static fromMouseEvent(
    event: MouseEvent, 
    canvas: HTMLCanvasElement
  ): CanvasCoordinate {
    const rect = canvas.getBoundingClientRect();
    
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      space: CoordinateSpace.CANVAS,
      precision: 'pixel',
      timestamp: performance.now()
    };
  }
  
  /**
   * Create canvas coordinate from character box (typesetter response)
   */
  static fromCharacterBox(
    charBox: [number, number, number, number],
    sourceSpace: CoordinateSpace = CoordinateSpace.BACKEND
  ): CanvasCoordinate {
    const [x, y, width, height] = charBox;
    
    // Transform coordinates based on source space
    if (sourceSpace === CoordinateSpace.HARFBUZZ) {
      // ✅ CARET FIX: Backend now sends correct pixel coordinates after font size fix
      // No scaling needed - coordinates are already in Canvas space
      return {
        x: x,
        y: y + height * 0.8, // Baseline position, no scaling
        space: CoordinateSpace.CANVAS,
        precision: 'subpixel',
        timestamp: performance.now()
      };
    }
    
    // Default: assume already in Canvas-compatible space
    return {
      x: x,
      y: y + height * 0.8, // Baseline position
      space: CoordinateSpace.CANVAS,
      precision: 'subpixel',
      timestamp: performance.now()
    };
  }
  
  /**
   * Transform HarfBuzz character boxes to Canvas coordinate space
   */
  static transformCharacterBoxes(
    charBoxes: number[][],
    sourceSpace: CoordinateSpace = CoordinateSpace.HARFBUZZ
  ): number[][] {
    if (sourceSpace !== CoordinateSpace.HARFBUZZ) {
      return charBoxes; // No transformation needed
    }
    
    // ✅ CARET FIX: Backend now sends correct pixel coordinates after font size fix
    // No scaling needed - coordinates are already in Canvas space
    return charBoxes.map(box => [
      box[0],  // x - no scaling
      box[1],  // y - no scaling  
      box[2],  // width - no scaling
      box[3]   // height - no scaling
    ]);
  }
  
  /**
   * Debug coordinate information
   */
  static debugCoordinate(coord: CanvasCoordinate, label: string = ''): void {
    console.log(`🔍 [COORDINATE_DEBUG] ${label}:`, {
      x: coord.x.toFixed(2),
      y: coord.y.toFixed(2),
      space: coord.space,
      precision: coord.precision,
      age: coord.timestamp ? `${(performance.now() - coord.timestamp).toFixed(2)}ms` : 'unknown'
    });
  }
  
  // ============================================================================
  // COORDINATE SPACE ANALYSIS
  // ============================================================================
  
  /**
   * Analyze coordinate space from typesetter response
   * 
   * This method validates our understanding of the coordinate system
   * by analyzing actual typesetter response data
   */
  static analyzeTypesetter(layoutPlan: any): {
    coordinateSpace: CoordinateSpace;
    confidence: number;
    evidence: string[];
  } {
    const evidence: string[] = [];
    let confidence = 0;
    
    if (layoutPlan.pageSetup?.margins?.left && layoutPlan.pages?.[0]?.frames?.[0]?.x) {
      const marginLeft = layoutPlan.pageSetup.margins.left;
      const frameX = layoutPlan.pages[0].frames[0].x;
      
      if (marginLeft === frameX) {
        evidence.push(`Frame.x (${frameX}) matches margins.left (${marginLeft})`);
        confidence += 0.3;
      }
    }
    
    if (layoutPlan.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0]?.charBoxes?.[0]) {
      const firstCharBox = layoutPlan.pages[0].frames[0].lines[0].runs[0].charBoxes[0];
      const frameX = layoutPlan.pages[0].frames[0].x;
      
      if (firstCharBox[0] === frameX) {
        evidence.push(`First charBox.x (${firstCharBox[0]}) matches frame.x (${frameX})`);
        confidence += 0.4;
      }
    }
    
    // Additional validation logic...
    
    return {
      coordinateSpace: confidence > 0.5 ? CoordinateSpace.PAGE : CoordinateSpace.CONTENT,
      confidence,
      evidence
    };
  }
}
